import { BehaviorSubject } from 'rxjs';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Attachment } from '../types';
import { v4 } from 'uuid';
import { addError } from './Error';
import { initAttachment } from '../Api/Attachments/useAttachmentsHelpers';
import {
  fetchAttachments,
  uploadAttachment,
  downloadAttachment as apiDownloadAttachment,
  deleteAttachment as apiDeleteAttachment,
  AttachmentUploadResponse
} from '../Api/Attachments/attachmentsApi';
import { useSnackbar } from '../Context/SnackbarContext';

export interface AttachmentReturn {
  queuedAttachments: Attachment[];
  uploadedAttachments: Attachment[];
  uploadAttachments: (files: File[]) => void;
  cancelUpload: (attachmentId: string) => void;
  retryUpload: (attachmentId: string) => void;
  downloadAttachment: (attachmentId: string) => Promise<string | null | undefined>;
  deleteAttachment: (attachmentId: string) => Promise<boolean | undefined>;
  upload: () => void;
  fetch: (id: string) => Promise<void>;
  setParentId: Dispatch<SetStateAction<string>>;
  clear: () => void;
}

const emitErrorState = (message?: string): void => addError('attachments', message);

const attachmentsSubject = new BehaviorSubject<Attachment[]>([]);
const queuedAttachmentsSubject = new BehaviorSubject<Attachment[]>([]);
const loadingSubject = new BehaviorSubject<boolean>(false);
const uploadingSubject = new BehaviorSubject<boolean>(false);

export const getAttachmentsSubject = (): BehaviorSubject<Attachment[]> => attachmentsSubject;
export const getQueuedAttachmentsSubject = (): BehaviorSubject<Attachment[]> => queuedAttachmentsSubject;
export const getAttachmentsLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;
export const getAttachmentsUploadingSubject = (): BehaviorSubject<boolean> => uploadingSubject;

export const getAttachments = (): Attachment[] => attachmentsSubject.value;
export const getQueuedAttachments = (): Attachment[] => queuedAttachmentsSubject.value;
export const getAttachmentsByParentId = (id: string): Attachment[] =>
  attachmentsSubject.value.filter((att: Attachment) => att.parentId === id);

export const addAttachments = (attachments: Attachment[]): void =>
  attachmentsSubject.next([...attachmentsSubject.value, ...attachments]);

export const addQueuedAttachments = (attachments: Attachment[]): void =>
  queuedAttachmentsSubject.next([...queuedAttachmentsSubject.value, ...attachments]);

export const setQueuedAttachments = (attachments: Attachment[]): void => queuedAttachmentsSubject.next(attachments);

export const cancelQueuedAttachment = (id: string): void =>
  queuedAttachmentsSubject.next(queuedAttachmentsSubject.value.filter((att) => att.id !== id));

export const removeAttachment = (id: string): void =>
  attachmentsSubject.next(attachmentsSubject.value.filter((att) => att.id !== id));

export function useAttachments(): AttachmentReturn {
  const [parentId, setParentId] = useState<string>('');
  const [localQueuedAttachments, setLocalQueuedAttachments] = useState<Attachment[]>([]);
  const [localUploadedAttachments, setLocalUploadedAttachments] = useState<Attachment[]>([]);
  const { setSnack } = useSnackbar();

  const uploadAttachments = (files: File[]) => {
    const newQueuedAttachments = files?.map((file) => initAttachment(file, v4(), parentId));
    if (newQueuedAttachments) addQueuedAttachments(newQueuedAttachments);
  };

  const cancelUpload = (attachmentId: string) => {
    const attachment = localQueuedAttachments?.find((a) => a.id === attachmentId);
    if (attachment) {
      attachment.cancelSource.cancel();
      cancelQueuedAttachment(attachmentId);
    }
  };

  const retryUpload = (attachmentId: string) => {
    const newQueuedAttachments = [...localQueuedAttachments];
    const attachmentIndex = newQueuedAttachments?.findIndex((a) => a.id === attachmentId);

    const newAttachmentObj = initAttachment(newQueuedAttachments[attachmentIndex].file, attachmentId, parentId);
    newQueuedAttachments[attachmentIndex] = newAttachmentObj;

    setQueuedAttachments(newQueuedAttachments);
  };

  const downloadAttachment = async (attachmentId: string): Promise<string | null | undefined> => {
    const response = await apiDownloadAttachment(attachmentId);
    if (response.error || response.data?.size === 0) return null;

    return URL.createObjectURL(response.data);
  };

  const deleteAttachment = async (attachmentId: string): Promise<boolean | undefined> => {
    const response = await apiDeleteAttachment(attachmentId);

    if (response.error) {
      return false;
    }

    removeAttachment(attachmentId);
    setLocalUploadedAttachments(getAttachmentsByParentId(parentId));
    return true;
  };

  const updateQueuedAttachments = (a: Attachment) => {
    const attachmentUpdate = [...localQueuedAttachments];
    const attachmentIndex = attachmentUpdate.indexOf(a);
    attachmentUpdate[attachmentIndex] = a;
    setQueuedAttachments(attachmentUpdate);
  };

  const upload = async () => {
    localQueuedAttachments
      ?.filter((queuedAttachment) => queuedAttachment.status === 'queued')
      ?.map(async (attachment) => {
        if (attachment.file && attachment.parentId) {
          uploadingSubject.next(true);
          attachment.status = 'uploading';
          updateQueuedAttachments(attachment);

          const progressHandler = (progressEvent: { loaded: number; total: number }) => {
            const { loaded, total } = progressEvent;
            attachment.progress = Math.min(Math.round((loaded / total) * 100), 95);
            updateQueuedAttachments(attachment);
          };
          const res = await uploadAttachment(attachment, progressHandler, attachment.parentId);

          if (res.error) {
            const isCancel = res.error.message === 'canceled';
            attachment.status = isCancel ? 'canceled' : 'error';
          } else if (res.data) {
            Object.assign(attachment, {
              ...(res.data as AttachmentUploadResponse),
              status: 'success',
              progress: 100
            });
            addAttachments([attachment]);
            setLocalUploadedAttachments(getAttachmentsByParentId(attachment.parentId));
            setSnack({ message: 'Your attachments have been uploaded successfully', type: 'success', open: true });
          }

          updateQueuedAttachments(attachment);
          uploadingSubject.next(false);
        }
      });
  };

  const fetch = async (id: string) => {
    if (!id) return;

    emitErrorState();
    loadingSubject.next(true);
    setLocalUploadedAttachments([]);
    setQueuedAttachments([]);

    const relatedAttachments = getAttachmentsByParentId(id);
    if (relatedAttachments?.length) {
      setLocalUploadedAttachments(relatedAttachments);
    } else {
      const { error, data } = await fetchAttachments(id);

      if (error) emitErrorState(error.message);

      if (data) {
        addAttachments(data);
        setLocalUploadedAttachments(getAttachmentsByParentId(id));
      }
    }

    loadingSubject.next(false);
  };

  const clear = () => {
    attachmentsSubject.next([]);
    queuedAttachmentsSubject.next([]);
  };

  useEffect(() => {
    const takeAction = async () => {
      upload();
    };
    takeAction();
  }, [localQueuedAttachments]);

  useEffect(() => {
    const attachmentSubscription = getQueuedAttachmentsSubject().subscribe((queuedAttachments) => {
      setLocalQueuedAttachments(queuedAttachments);
    });

    return () => {
      if (attachmentSubscription) attachmentSubscription.unsubscribe();
    };
  }, []);

  return {
    queuedAttachments: localQueuedAttachments,
    uploadedAttachments: localUploadedAttachments,
    uploadAttachments,
    cancelUpload,
    retryUpload,
    downloadAttachment,
    deleteAttachment,
    upload,
    fetch,
    setParentId,
    clear
  };
}
